<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("servicios.grid.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearServicio()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="gridProperties" :data-source="gridSource" ref="grid">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="Nombre" :caption="$t('servicios.grid.nombre')"></DxColumn>
      <DxColumn data-field="FechaInicio" :caption="$t('servicios.grid.fecha_inicio')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="FechaFin" :caption="$t('servicios.grid.fecha_fin')" data-type="date" format="dd/MM/yyyy"> </DxColumn>
      <DxColumn data-field="TipoEnergia" :caption="$t('servicios.grid.tipo_energia')">
        <DxLookup column-auto-width="true" v-bind="getLookupTipoEnergia()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Coste" :caption="$t('servicios.grid.coste')" data-type="number" cell-template="costeTemplate"></DxColumn>
      <DxColumn data-field="AplicaPersonaFisica" :caption="$t('servicios.grid.aplica_fisica')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="AplicaPersonaJuridica" :caption="$t('servicios.grid.aplica_juridica')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Activa" :caption="$t('servicios.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookupEstadoActivacion()"></DxLookup>
      </DxColumn>
      <DxColumn type="buttons" :caption="$t('servicios.grid.acciones')" :fixed="true" fixed-position="right" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('servicios.grid.duplicar'),
          icon: 'fa-solid fa-clone text-success',
          onClick: (e: any) => duplicar(e.row?.data.Id),
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('servicios.grid.modificar'),
          icon: 'fa-regular fa-edit text-success',
          onClick: (e: any) => verDetalle(e.row?.data.Id),
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('servicios.grid.borrar'),
          icon: 'fa-regular fa-trash text-success',
          onClick: (e: any) => borrar(e.row?.data.Id),
          disabled: (options: any) => options.row?.data.TieneTramitesAsociados,
        },
      ]" />

      <template #estadoTemplate="{ data }">
        <div :class="[`d-inline-block rounded py-1 px-2`, `estado ${data.value ? 'activado' : 'desactivado'}`]">
          {{ $t(`servicios.estado.${data.value ? "activado" : "desactivado"}`) }}
        </div>
      </template>
      <template #costeTemplate="{ data }">
        <span class="coste">{{ data.value }} €/mes</span>
      </template>
      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, type Component } from "vue";
import { DxDataGrid, DxColumn, DxLookup, DxColumnFixing } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getLookup, getColumnLookupBoolean } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import type { ColumnLookup } from "devextreme/ui/data_grid";
import { type DXLoadOptions } from "@api/api";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import { useModalService } from "@services/modal-service";
import { messageBoxResult } from "@components/dialogs/message-box";
import ServicioEnergiaTabPanel from "@/components/servicios-energia/base/ServicioEnergiaTabPanel.vue";
import ServicioEndesaTabPanel from "@/components/servicios-energia/endesa/ServicioEndesaTabPanel.vue";
import { CreateServicioEnergia } from "@/components/servicios-energia/base/models/CreateServicioEnergia";
import { CreateServicioEndesa } from "@/components/servicios-energia/endesa/models/CreateServicioEndesa";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { Constants } from "@/.shared/constants/OperacionEnergia";
import { modalMaxHeight, xlModalPopupProps } from "@/.shared/helpers/modal-helper";

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const gridProperties = ref<DxDataGrid>();

const props = defineProps({
  idEmpresaServicios: String,
});

const defaultPopUpProps = { ...xlModalPopupProps, minHeight: modalMaxHeight };

onMounted(async () => {
  setDatasource();
});

function getLookupEstadoActivacion(): ColumnLookup {
  return {
    dataSource: [
      {
        value: true,
        text: intl.i18n.t(`servicios.estado.activado`),
      },
      {
        value: false,
        text: intl.i18n.t(`servicios.estado.desactivado`),
      },
    ],
    valueExpr: "value",
    displayExpr: "text",
  };
}

function getLookupTipoEnergia(): ColumnLookup {
  return getLookup("servicios", "tipo_energia");
}

async function crearServicio() {
  let title = `${intl.i18n.t("servicios.creacion.titulo")}`;

  var detalleEmpresaServicios = await api.callStandardApi("getEmpresaServiciosEnergiaById", { showLoading: false }, props.idEmpresaServicios!);
  let nombreEmpresaServicios = detalleEmpresaServicios.Nombre;

  let modalComponent: Component;
  let modalProps: Record<string, unknown>;

  if (
    Constants.EmpresaServicios.EMPRESAS_ENDESA.includes(props.idEmpresaServicios!)
  ) {
    modalComponent = ServicioEndesaTabPanel;
    modalProps = {
      model: new CreateServicioEndesa({ IdEmpresaServicios: props.idEmpresaServicios, EmpresaServicios: nombreEmpresaServicios }),
      isEditMode: false,
      tieneTramitesAsociados: false,
    };
  } else {
    modalComponent = ServicioEnergiaTabPanel;
    modalProps = {
      model: new CreateServicioEnergia({ IdEmpresaServicios: props.idEmpresaServicios, EmpresaServicios: nombreEmpresaServicios }),
      isEditMode: false,
      tieneTramitesAsociados: false,
    };
  }

  modal.showModal(modalComponent, modalProps, { ...defaultPopUpProps, title: title }).then((value) => {
    if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
  });
}

const setDatasource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      if (q.filter != null && q.filter.length >= 0) q.filter = [["IdEmpresaServicios", "=", props.idEmpresaServicios], "and", q.filter];
      else q.filter = ["IdEmpresaServicios", "=", props.idEmpresaServicios];

      return await api.callStandardApi("getServiciosEnergiaForGrid", { showLoading: false }, q);
    },
  });
};

const duplicar = async (idServicio: string) => {
  await api
    .callStandardApi("duplicateServicioEnergia", { showLoading: true }, idServicio)
    .then(() => {
      toastSuccess(intl.i18n.t("servicio_energia.duplicacion_exito"));
      grid.value?.instance?.refresh();
    })
    .catch(() => toastError(intl.i18n.t("servicio_energia.duplicacion_fallo")));
};

const verDetalle = async (idServicio: string) => {
  let title = `${intl.i18n.t("servicio_energia.edicion.titulo")}`;
  let modalComponent: Component;
  let modalProps: Record<string, unknown>;

  if (Constants.EmpresaServicios.EMPRESAS_ENDESA.includes(props.idEmpresaServicios!)) {
    let value = await api.callStandardApi("getServicioEndesaById", { showLoading: true }, idServicio);

    modalComponent = ServicioEndesaTabPanel;
    modalProps = {
      model: new CreateServicioEndesa().createFromServicioDetail(value),
      isEditMode: true,
      tieneTramitesAsociados: value.TieneTramitesAsociados,
    };
  } else {
    let value = await api.callStandardApi("getServicioEnergiaById", { showLoading: true }, idServicio);

    modalComponent = ServicioEnergiaTabPanel;
    modalProps = { model: new CreateServicioEnergia().createFromServicioDetail(value), isEditMode: true, tieneTramitesAsociados: value.TieneTramitesAsociados };
  }

  let response = await modal.showModal(modalComponent, modalProps, { title: title, ...defaultPopUpProps });

  if (response == messageBoxResult.ok) grid.value?.instance?.refresh();
};

const borrar = async (idServicio: string) => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("servicios.grid.borrar_pregunta"),
    intl.i18n.t("servicios.grid.titulo_confirma_borrar"),
    "okCancel",
    "warning",
  );
  if (respuesta == messageBoxResult.ok)
    await api
      .callStandardApi("deleteServicioEnergia", {}, idServicio)
      .then(() => {
        toastSuccess(intl.i18n.t("servicio_energia.borrado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("servicio_energia.borrado_fallo")));
};

onMounted(() => {
  gridProperties.value = getDefaultGridProperties();
});
</script>

<style lang="scss" scoped></style>
